import { Chip, Stack, Tooltip, Typography } from '@mui/material';
import { useTranslation } from '../../contexts/TranslationContext';
import { FileMetadata } from '../../api/types/cms';
import { useDateFormatter } from '../../utils/date';
import { useTheme } from '@mui/material/styles';

export default function AutomaticPublishingFileChip({
    version,
    variant = 'list',
}: {
    version: FileMetadata;
    variant?: 'list' | 'overview';
}) {
    const theme = useTheme();

    const { translate } = useTranslation();
    const { formatDate } = useDateFormatter();

    const renderComponent = () => {
        if (variant === 'overview') {
            return (
                <Typography
                    variant="caption"
                    color={theme.palette.getContrastText(
                        theme.palette.info.main,
                    )}>
                    {translate('pages.cms.files.auto_publishing')}
                </Typography>
            );
        }
        return (
            <Typography
                variant="caption"
                color={theme.palette.getContrastText(theme.palette.info.main)}>
                {translate('pages.cms.files.dates_info')}
            </Typography>
        );
    };

    const renderTooltip = () => {
        return (
            <Stack spacing={1}>
                {version.start_date && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                            variant="caption"
                            color={theme.palette.getContrastText(
                                theme.palette.info[theme.palette.mode],
                            )}
                            component="p">
                            {translate('pages.cms.files.start_date')}
                        </Typography>
                        <Typography
                            variant="caption"
                            color={theme.palette.getContrastText(
                                theme.palette.info[theme.palette.mode],
                            )}
                            component="p">
                            {formatDate(version.start_date)}
                        </Typography>
                    </Stack>
                )}
                {version.end_date && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                            variant="caption"
                            color={theme.palette.getContrastText(
                                theme.palette.info[theme.palette.mode],
                            )}
                            component="p">
                            {translate('pages.cms.files.end_date')}
                        </Typography>
                        <Typography
                            variant="caption"
                            color={theme.palette.getContrastText(
                                theme.palette.info[theme.palette.mode],
                            )}
                            component="p">
                            {formatDate(version.end_date)}
                        </Typography>
                    </Stack>
                )}
            </Stack>
        );
    };

    if (!version.uses_automatic_publishing) {
        return null;
    }

    if (variant === 'overview') {
        return (
            <Chip
                label={renderComponent()}
                color="info"
                size="small"
                sx={{ borderRadius: 5 }}
            />
        );
    }

    return (
        <Tooltip
            title={renderTooltip()}
            slotProps={{
                tooltip: {
                    sx: { background: theme.palette.info[theme.palette.mode] },
                },
            }}
            placement="top">
            <Chip
                label={renderComponent()}
                color="info"
                size="small"
                sx={{ borderRadius: 5 }}
            />
        </Tooltip>
    );
}
